import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import 'jspdf-autotable';  // Add this import for better tables

export class PDFGenerator {
  static async generateMaintenanceReport(data) {
    // Create PDF document in landscape for more space
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Helper function to add section title
    const addSectionTitle = (text, y) => {
      doc.setFillColor(52, 144, 220); // Blue background
      doc.setTextColor(255, 255, 255); // White text
      doc.setFontSize(12);
      doc.rect(10, y, 277, 8, 'F');
      doc.text(text, 12, y + 5.5);
      doc.setTextColor(0, 0, 0); // Reset to black text
      return y + 8; // Return next Y position
    };

    // Add header with QR code
    const qrCodeDataUrl = await QRCode.toDataURL(data.reportId);
    doc.addImage(qrCodeDataUrl, 'PNG', 245, 5, 35, 35);
    
    // Add company logo (if available)
    // doc.addImage(logoUrl, 'PNG', 10, 5, 40, 15);
    
    // Report title and basic info
    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80); // Dark blue text
    doc.text('Maintenance Report', 10, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100); // Gray text
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 27);
    doc.text(`Status: ${data.ticket.status.toUpperCase()}`, 10, 32);

    let yPos = 45; // Starting Y position for content

    // Ticket and Asset Info in a 2-column layout
    yPos = addSectionTitle('Ticket & Asset Information', yPos);
    
    // Create two-column table for ticket and asset info
    doc.autoTable({
      startY: yPos,
      theme: 'plain',
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 100 },
        2: { cellWidth: 30 },
        3: { cellWidth: 100 }
      },
      body: [
        [
          { content: 'Ticket ID:', styles: { fontStyle: 'bold' } },
          data.ticket.id,
          { content: 'Asset ID:', styles: { fontStyle: 'bold' } },
          data.asset.id
        ],
        [
          { content: 'Title:', styles: { fontStyle: 'bold' } },
          data.ticket.title,
          { content: 'Asset Name:', styles: { fontStyle: 'bold' } },
          data.asset.name
        ],
        [
          { content: 'Priority:', styles: { fontStyle: 'bold' } },
          data.ticket.priority.toUpperCase(),
          { content: 'Model:', styles: { fontStyle: 'bold' } },
          data.asset.model
        ],
        [
          { content: 'Assigned To:', styles: { fontStyle: 'bold' } },
          data.maintenance.assignedTo.name,
          { content: 'Serial Number:', styles: { fontStyle: 'bold' } },
          data.asset.serialNumber
        ]
      ],
      margin: { left: 10 }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Location and Cost Info
    yPos = addSectionTitle('Location & Cost Details', yPos);
    
    doc.autoTable({
      startY: yPos,
      theme: 'plain',
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 100 },
        2: { cellWidth: 30 },
        3: { cellWidth: 100 }
      },
      body: [
        [
          { content: 'Facility:', styles: { fontStyle: 'bold' } },
          data.location.facility,
          { content: 'Est. Cost:', styles: { fontStyle: 'bold' } },
          `$${data.costs.estimated}`
        ],
        [
          { content: 'Building:', styles: { fontStyle: 'bold' } },
          data.location.building,
          { content: 'Actual Cost:', styles: { fontStyle: 'bold' } },
          `$${data.costs.actual}`
        ],
        [
          { content: 'Floor:', styles: { fontStyle: 'bold' } },
          data.location.floor,
          { content: 'Department:', styles: { fontStyle: 'bold' } },
          data.maintenance.assignedTo.department || 'N/A'
        ]
      ],
      margin: { left: 10 }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Problem Description
    yPos = addSectionTitle('Problem Details', yPos);
    
    doc.autoTable({
      startY: yPos,
      theme: 'plain',
      columnStyles: {
        0: { cellWidth: 270 }
      },
      body: [
        [{ content: data.maintenance.description, styles: { cellPadding: 2 } }]
      ],
      margin: { left: 10 }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Timeline in a compact table
    if (data.maintenance.timeline?.length > 0) {
      yPos = addSectionTitle('Maintenance Timeline', yPos);
      
      const timelineData = data.maintenance.timeline.map(event => [
        event.date,
        event.action,
        event.user,
        event.details || ''
      ]);

      doc.autoTable({
        startY: yPos,
        theme: 'striped',
        headStyles: { fillColor: [241, 196, 15], textColor: [0, 0, 0] },
        head: [['Date', 'Action', 'User', 'Details']],
        body: timelineData,
        margin: { left: 10 },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 60 },
          2: { cellWidth: 40 },
          3: { cellWidth: 130 }
        }
      });
    }

    // Add page number if content exceeds one page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 20,
        doc.internal.pageSize.height - 10
      );
    }

    return doc.output('blob');
  }
} 